import type { PxRect } from '@mado/schema/camera';
import { sentences } from '@mado/schema/showcase';
import { el } from './stage';

/*
 * The phone walkthrough (founder, 2026-10-21; the default since 2026-10-22, after an iPhone pass):
 * the 9:16 video's layout. A window with the screen's own shape, the step's headline above it and
 * its sentences as cards under it, the three centred as one block. Part of player.js, so a phone
 * paints this layout from its first frame; its styles are in styles.ts (`.mado-cards`).
 */

/** Where a step's window and headline sit (a showcase: the window only, no headline). */
export interface StoryPlan {
  /** The framed window every layout is laid out in. */
  picture: PxRect;
  /** The headline block above it (height 0: nothing to say). */
  caption: PxRect;
  /** Corner radius of the window. */
  radius: number;
}

/**
 * The headline and the window's top are the same for every step (the tallest headline decides, and
 * `top` pins the block); the window ends where that step's own sentences need it to (founder,
 * 2026-10-22: room kept for the longest step's sentences left the others a small screen). Pure.
 */
export interface CardsPlan extends StoryPlan {
  /** Where the sentence cards go, under the window. */
  cards: PxRect;
}

export const CARDS_MARGIN_PX = 16;
/**
 * Room kept for the control bar at the foot: 64 px in click-through, 90 with Watch's scrub row
 * (measured on a 375 px phone, 2026-10-22). The larger always, so starting Watch moves nothing.
 */
export const CARDS_CHROME_PX = 92;
const CARDS_GAP_PX = 14;
const CARDS_RADIUS_PX = 14;
/** The tallest window, width over height: a portrait close-up of a desktop, as the hand target frames them. */
const CARDS_TALLEST = 0.62;

export function planCards(
  viewport: { w: number; h: number },
  /** The screens' shape: width over height (the median over the demo's steps). */
  shape: number,
  headH: number,
  cardsH: number,
  /** Where the block starts, the same for every step (`cardsTop`); absent: centred. */
  top?: number,
): CardsPlan {
  const w = viewport.w - CARDS_MARGIN_PX * 2;
  const room = cardsRoom(viewport.h);
  const head = headH > 0 ? headH + 4 : 0;
  const below = cardsH > 0 ? cardsH + CARDS_GAP_PX : 0;
  // Every pixel the words leave (founder, 2026-10-22: the screen's own shape left a desktop's
  // close-ups a strip 217 px tall on an iPhone and a third of the phone empty). The phone track is
  // planned for this window, so a close-up fills it; a whole screen sits inside it, never past
  // it at its width. Never taller than a portrait screen (`CARDS_TALLEST`), or a phone capture's.
  const h = Math.max(
    Math.min(w * 0.5, 120),
    Math.min(
      Math.floor(w / Math.min(Math.max(0.2, shape), CARDS_TALLEST)),
      room - head - below - CARDS_GAP_PX,
    ),
  );
  const y = (top ?? cardsTop(room, head + h + below)) + head;
  return {
    picture: { x: CARDS_MARGIN_PX, y, w, h },
    caption: { x: 0, y: y - head, w: viewport.w, h: headH },
    cards: { x: CARDS_MARGIN_PX, y: y + h + CARDS_GAP_PX, w, h: cardsH },
    radius: CARDS_RADIUS_PX,
  };
}

/** The block's top: centred in the room above the bar (the tallest step's block, for every step). */
export function cardsTop(room: number, block: number): number {
  return Math.max(4, Math.round((room - block) / 2));
}

/** The room above the control bar on a viewport `h` tall. */
export function cardsRoom(h: number): number {
  return h - CARDS_CHROME_PX - 8;
}

/** What a step's sentence cards say: none when its one sentence only repeats the headline. */
export function cardLines(said: string, headline: string): string[] {
  const t = said.trim();
  if (!t) return [];
  const lines = sentences(t);
  const norm = (x: string): string =>
    x
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  return lines.length <= 1 && norm(t) === norm(headline) ? [] : lines;
}

/**
 * The step's sentences under the window, one card each, the one being said lit (the rest faint).
 * In click-through the lit card carries the callout's Back, Skip and Next (`foot`).
 */
export class SentenceCards {
  readonly root: HTMLElement;
  private readonly measure: HTMLElement;
  private key = '';

  constructor() {
    this.root = el('div', 'mado-cards');
    this.measure = el('div', 'mado-cards mado-cards--measure');
    this.hide();
  }

  mount(parent: HTMLElement, before: Node | null): void {
    parent.insertBefore(this.root, before);
    parent.insertBefore(this.measure, before);
  }

  private fill(into: HTMLElement, lines: readonly string[]): void {
    into.textContent = '';
    lines.forEach((l, i) => {
      const c = el('div', 'mado-sent');
      const n = el('span', 'mado-sent-n');
      n.textContent = String(i + 1);
      const t = el('span', 'mado-sent-t');
      t.textContent = l;
      c.append(n, t);
      into.appendChild(c);
    });
  }

  /** Height the cards need `w` wide (a Next button's row included, for the lit one). */
  heightFor(lines: readonly string[], w: number): number {
    if (!lines.length) return 0;
    this.measure.style.width = `${w}px`;
    this.fill(this.measure, lines);
    // The foot of the lit card adds a row; reserve it on the tallest card.
    return Math.ceil(this.measure.offsetHeight) + 44;
  }

  show(lines: readonly string[], area: PxRect, on: number, foot: HTMLElement | null): void {
    if (!lines.length) return this.hide();
    const key = lines.join('|');
    if (key !== this.key) {
      this.key = key;
      this.fill(this.root, lines);
    }
    const s = this.root.style;
    s.display = '';
    s.left = `${area.x}px`;
    s.top = `${area.y}px`;
    s.width = `${area.w}px`;
    this.light(on, foot);
  }

  /** Take the lit card's buttons away; true when the keyboard was on one of them. */
  dropFoot(): boolean {
    const own = this.root.querySelector('.mado-callout-f');
    if (!own) return false;
    const had = own.contains(document.activeElement);
    own.remove();
    return had;
  }

  /** `on` below 0: nothing is being said, so every card shows plainly. */
  light(on: number, foot: HTMLElement | null): void {
    this.root.classList.toggle('mado-cards--all', on < 0);
    const cards = Array.from(this.root.children) as HTMLElement[];
    cards.forEach((c, i) => {
      c.classList.toggle('mado-sent--on', i === on);
      const own = c.querySelector('.mado-callout-f');
      if (own && (i !== on || own !== foot)) own.remove();
    });
    const lit = cards[on];
    if (lit && foot && foot.parentElement !== lit.lastElementChild) {
      (lit.querySelector('.mado-sent-t') as HTMLElement).appendChild(foot);
    }
  }

  hide(): void {
    this.key = '';
    this.root.textContent = '';
    this.root.style.display = 'none';
  }
}
